import { useMemo, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import type { AgencyMember, Role } from '@/types';

const roleMap: Record<Role, { label: string; heat: string }> = {
  SystemAdmin: { label: 'מנהל מערכת', heat: 'heat-hot' },
  AgencyAdmin: { label: 'מנהל סוכנות', heat: 'heat-closing' },
  Agent: { label: 'סוכן', heat: 'heat-cold' },
};

const statusMap: Record<string, { label: string; heat: string }> = {
  ACTIVE: { label: 'פעיל', heat: 'heat-closing' },
  SUSPENDED: { label: 'מושעה', heat: 'heat-lost' },
};

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

type SortKey = 'name' | 'role' | 'status' | 'created_at';

interface MemberTableProps {
  members: AgencyMember[];
  isLoading: boolean;
  canManage: boolean;
  onEditMember?: (member: AgencyMember) => void;
  onSuspendMember?: (member: AgencyMember) => void;
  onRemoveMember?: (member: AgencyMember) => void;
  onTransferAdmin?: (member: AgencyMember) => void;
  onShowStatus?: (member: AgencyMember) => void;
}

/** A small transparent icon button used inside table rows. */
function IconBtn({
  title,
  onClick,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="btn ghost icon-only sm"
      title={title}
      aria-label={title}
      onClick={onClick}
      style={danger ? { color: 'var(--destructive)' } : undefined}
    >
      {children}
    </button>
  );
}

export function MemberTable({
  members,
  isLoading,
  canManage,
  onEditMember,
  onSuspendMember,
  onRemoveMember,
  onTransferAdmin,
  onShowStatus,
}: MemberTableProps) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [removeTarget, setRemoveTarget] = useState<AgencyMember | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<AgencyMember | null>(null);
  const [transferTarget, setTransferTarget] = useState<AgencyMember | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filtered = useMemo(() => {
    let result = members;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.username.toLowerCase().includes(s) ||
          m.name?.toLowerCase().includes(s) ||
          m.email?.toLowerCase().includes(s) ||
          m.mobile?.includes(s) ||
          roleMap[m.role]?.label.includes(s),
      );
    }
    return [...result].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'name') return (a.name || a.username).localeCompare(b.name || b.username, 'he') * dir;
      if (sortKey === 'role') return a.role.localeCompare(b.role) * dir;
      if (sortKey === 'status') return (a.status || 'ACTIVE').localeCompare(b.status || 'ACTIVE') * dir;
      return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir;
    });
  }, [members, search, sortKey, sortDir]);

  const handleExportExcel = useCallback(() => {
    const rows = filtered.map((m) => ({
      'שם': m.name || '—',
      'אימייל': m.email || m.username,
      'תפקיד': roleMap[m.role]?.label || m.role,
      'סטטוס': statusMap[m.status || 'ACTIVE']?.label || m.status,
      'טלפון נייד': m.mobile || '—',
      'אימות אימייל': m.email_verified ? 'מאומת' : 'לא מאומת',
      'תאריך יצירה': formatDate(m.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'משתמשים');
    XLSX.writeFile(wb, `SmartBot_Users_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [filtered]);

  return (
    <div className="leads-card">
      <div className="leads-card-header">
        <div className="search-and-tools">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              className="leads-search"
              placeholder="חיפוש לפי שם, אימייל, תפקיד, טלפון…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="btn outline" onClick={handleExportExcel} disabled={filtered.length === 0}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            ייצוא לאקסל
          </button>
        </div>
      </div>

      <div className="leads-card-body">
        <div className="quote-table-scroll">
          <table className="crm-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>שם / אימייל{arrow('name')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('role')}>תפקיד{arrow('role')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>סטטוס{arrow('status')}</th>
                <th>טלפון נייד</th>
                <th style={{ textAlign: 'center' }}>אימות אימייל</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('created_at')}>תאריך יצירה{arrow('created_at')}</th>
                {canManage && <th style={{ textAlign: 'center' }}>פעולות</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6}>
                    <div className="empty">טוען…</div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6}>
                    <div className="empty">לא נמצאו משתמשים</div>
                  </td>
                </tr>
              ) : (
                filtered.map((member) => {
                  const roleInfo = roleMap[member.role] || roleMap.Agent;
                  const memberStatus = member.status || 'ACTIVE';
                  const statusInfo = statusMap[memberStatus] || statusMap.ACTIVE;
                  const isSuspended = memberStatus === 'SUSPENDED';
                  const isAdminRole = member.role === 'AgencyAdmin' || member.role === 'SystemAdmin';

                  return (
                    <tr key={member.user_id} style={isSuspended ? { opacity: 0.6 } : undefined}>
                      <td>
                        <div className="cell-primary">{member.name || '—'}</div>
                        <div className="cell-sub" dir="ltr">{member.email || member.username}</div>
                      </td>
                      <td>
                        <span className={`stage-badge ${roleInfo.heat}`}>{roleInfo.label}</span>
                      </td>
                      <td>
                        <span className={`stage-badge ${statusInfo.heat}`}>{statusInfo.label}</span>
                      </td>
                      <td className="cell-sub" dir="ltr">{member.mobile || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {member.email_verified ? (
                          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#16a34a" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        ) : (
                          <span className="cell-sub">—</span>
                        )}
                      </td>
                      <td className="cell-sub">{formatDate(member.created_at)}</td>
                      {canManage && (
                        <td>
                          <div className="row-actions" style={{ justifyContent: 'center' }}>
                            {onShowStatus && (
                              <IconBtn title="סטטוס הזמנה" onClick={() => onShowStatus(member)}>
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                              </IconBtn>
                            )}
                            {onEditMember && (
                              <IconBtn title="עריכה" onClick={() => onEditMember(member)}>
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                              </IconBtn>
                            )}
                            {onTransferAdmin && !isAdminRole && !isSuspended && (
                              <IconBtn title="הפוך למנהל סוכנות" onClick={() => setTransferTarget(member)}>
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16" /></svg>
                              </IconBtn>
                            )}
                            {onSuspendMember && !isAdminRole && (
                              <IconBtn title={isSuspended ? 'שחזר' : 'השעה'} danger={!isSuspended} onClick={() => setSuspendTarget(member)}>
                                {isSuspended ? (
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#16a34a" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5" /></svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m4.9 4.9 14.2 14.2" /></svg>
                                )}
                              </IconBtn>
                            )}
                            {onRemoveMember && !isAdminRole && (
                              <IconBtn title="הסר משתמש" danger onClick={() => setRemoveTarget(member)}>
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM17 11h6" /></svg>
                              </IconBtn>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {removeTarget && (
        <ConfirmModal
          title="הסרת משתמש מהסוכנות"
          confirmLabel="הסר משתמש"
          danger
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => {
            onRemoveMember?.(removeTarget);
            setRemoveTarget(null);
          }}
        >
          האם להסיר את <strong>{removeTarget.name || removeTarget.username}</strong> מהסוכנות? המשתמש לא יוכל עוד להתחבר.
        </ConfirmModal>
      )}

      {suspendTarget && (
        <ConfirmModal
          title={suspendTarget.status === 'SUSPENDED' ? 'שחזור משתמש' : 'השעיית משתמש'}
          confirmLabel={suspendTarget.status === 'SUSPENDED' ? 'שחזר' : 'השעה'}
          danger={suspendTarget.status !== 'SUSPENDED'}
          onClose={() => setSuspendTarget(null)}
          onConfirm={() => {
            onSuspendMember?.(suspendTarget);
            setSuspendTarget(null);
          }}
        >
          {suspendTarget.status === 'SUSPENDED' ? (
            <>האם לשחזר את <strong>{suspendTarget.name || suspendTarget.username}</strong>? המשתמש יוכל להתחבר שוב.</>
          ) : (
            <>האם להשעות את <strong>{suspendTarget.name || suspendTarget.username}</strong>? המשתמש לא יוכל להתחבר.</>
          )}
        </ConfirmModal>
      )}

      {transferTarget && (
        <ConfirmModal
          title="הפיכה למנהל סוכנות"
          confirmLabel="הפוך למנהל"
          onClose={() => setTransferTarget(null)}
          onConfirm={() => {
            onTransferAdmin?.(transferTarget);
            setTransferTarget(null);
          }}
        >
          האם להפוך את <strong>{transferTarget.name || transferTarget.username}</strong> למנהל סוכנות? למשתמש יינתנו הרשאות ניהול מלאות בסוכנות.
        </ConfirmModal>
      )}
    </div>
  );
}

/** Lightweight confirmation modal reusing the app's native modal styles. */
function ConfirmModal({
  title,
  confirmLabel,
  danger,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="סגור">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="modal-body" style={{ lineHeight: 1.7 }}>{children}</div>
        <div className="modal-footer">
          <button className="btn outline" onClick={onClose}>ביטול</button>
          <div className="spacer" />
          <button className={danger ? 'btn danger' : 'btn'} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
